import { useMemo } from 'react'
import { DonutChartData, DonutLegendItem } from '../../../../shared/components/DonutCharts'
import { filterEmptyDonutCategories } from '../../../../shared/components/DonutCharts/hooks/filterEmptyCategories'
import {
    useGetTppElementSupportQuery,
    TppCellMetric,
} from '../../../../shared/queries/metrics/useGetTppElementSupportQuery'
import useActivityStatuses from '../../../../shared/hooks/useActivityStatuses'
import { COLORS } from '../../../../style/colors'
import { getActivityStatusDetailsIcon } from '../../../../shared/hooks/useActivityStatuses/useActivityStatuses.helpers'
import useActivitiesDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'

const UNLINKED_ID = 'Unlinked'
export const NO_STATUS_ID = 'NoStatus'
export const UNCATEGORIZED_ID = 'Uncategorized'

// Presentation for the two synthetic buckets. Shared with TppCellDetailList so the donuts and
// the detail list describe an activity the same way without the list depending on this legend.
export const NO_STATUS_LEGEND_ITEM: DonutLegendItem = {
    id: NO_STATUS_ID,
    label: 'No Status',
    color: COLORS.granite,
    icon: getActivityStatusDetailsIcon({ shape: 'circle', color: COLORS.granite }),
}

export const UNCATEGORIZED_LEGEND_ITEM: DonutLegendItem = {
    id: UNCATEGORIZED_ID,
    label: 'Uncategorized',
    color: COLORS.granite,
    icon: getActivityStatusDetailsIcon({ shape: 'square', color: COLORS.granite }),
}

const useTppElementSupport = (
    columnId: string | null,
    highlightedOnly: boolean,
    sectionIds?: string[],
    drillDownSectionId?: string | null
) => {
    const getTppElementSupportQuery = useGetTppElementSupportQuery(
        columnId,
        highlightedOnly,
        drillDownSectionId ?? null
    )

    const queryData = getTppElementSupportQuery.data?.data
    const rowIds = getTppElementSupportQuery.data?.rowIds

    const { activityStatusDetails, getActivityStatusDetails } = useActivityStatuses()
    const activitiesDisplayName = useActivitiesDisplayName()

    const legend = useMemo((): DonutLegendItem[] => {
        const statusLegend = activityStatusDetails.map((statusDetails) => ({
            id: statusDetails.id,
            label: statusDetails.name,
            color: statusDetails.color,
            icon: statusDetails.icon,
        }))

        const unlinkedItem: DonutLegendItem = {
            id: UNLINKED_ID,
            label: `No ${activitiesDisplayName.plural}`,
            color: COLORS.pastelGrey,
            icon: getActivityStatusDetailsIcon({ shape: 'circle', color: COLORS.pastelGrey }),
        }

        return [...statusLegend, NO_STATUS_LEGEND_ITEM, UNCATEGORIZED_LEGEND_ITEM, unlinkedItem]
    }, [activityStatusDetails, activitiesDisplayName.plural])

    const sectionIdMap = useMemo(() => {
        const map: Record<string, string> = {}
        for (const entry of queryData ?? []) {
            if (entry.sectionId) map[entry.grouping] = entry.sectionId
        }
        return map
    }, [queryData])

    const data = useMemo((): DonutChartData<TppCellMetric>[] => {
        if (!queryData) {
            return []
        }

        const sorted = [...queryData]

        if (drillDownSectionId && rowIds?.length) {
            // In drill-down mode, sort by rowIds order
            sorted.sort((a, b) => {
                if (!a.rowId) return a.grouping === 'Unclassified' ? 1 : -1
                if (!b.rowId) return b.grouping === 'Unclassified' ? -1 : 1
                return rowIds.indexOf(a.rowId) - rowIds.indexOf(b.rowId)
            })
        } else if (sectionIds?.length) {
            // Sort entries by sectionIds order (All stays first, Unclassified stays last)
            sorted.sort((a, b) => {
                if (!a.sectionId) return a.grouping === 'All' ? -1 : 1
                if (!b.sectionId) return b.grouping === 'All' ? 1 : -1
                return sectionIds.indexOf(a.sectionId) - sectionIds.indexOf(b.sectionId)
            })
        }

        return sorted.map(({ grouping: title, sectionId, rowId, cells }: any) => {
            const isDrillable = drillDownSectionId != null ? true : !!sectionId || title === 'All'
            // Count cells per resolved status
            const statusCounts: Record<string, TppCellMetric[]> = {}

            for (const cell of cells) {
                let resolvedStatus: string

                if (!cell.activities || cell.activities.length === 0) {
                    resolvedStatus = UNLINKED_ID
                } else {
                    // Pick the status with the highest order value
                    let bestOrder = -1
                    resolvedStatus = NO_STATUS_ID
                    for (const activity of cell.activities) {
                        if (activity.status == null) {
                            // Truly no status (null/undefined) — lowest priority
                            if (bestOrder < 0) resolvedStatus = NO_STATUS_ID
                        } else {
                            const isRecognized = activityStatusDetails.some((s) => s.id === activity.status)
                            if (isRecognized) {
                                const details = getActivityStatusDetails(activity.status)
                                if (details.order > bestOrder) {
                                    bestOrder = details.order
                                    resolvedStatus = details.id
                                }
                            } else if (bestOrder < 0) {
                                // Empty string, "---", or any status not in the org's config
                                resolvedStatus = UNCATEGORIZED_ID
                            }
                        }
                    }
                }

                if (!statusCounts[resolvedStatus]) {
                    statusCounts[resolvedStatus] = []
                }
                statusCounts[resolvedStatus].push(cell)
            }

            const values = legend.map((legendItem) => {
                const items = statusCounts[legendItem.id] || []
                return { items, count: items.length, label: legendItem.label }
            })

            return { title, values, ...(isDrillable ? {} : { disableTotalClick: true as const }) }
        })
    }, [queryData, legend, getActivityStatusDetails, sectionIds, drillDownSectionId, rowIds])

    const { filteredLegend, filteredData } = useMemo(() => filterEmptyDonutCategories(legend, data), [legend, data])

    return {
        // Pruned to the statuses that actually won a cell — for the donut legend only, which
        // shouldn't list statuses that have no slice. Never use it to gate rendering of individual
        // activities: a cell is counted under its furthest-along activity, so a status can be
        // missing here while activities still carry it. Resolve those against useActivityStatuses.
        legend: filteredLegend,
        data: filteredData,
        isLoading: getTppElementSupportQuery.isLoading,
        sectionIdMap,
        getActivityStatusDetails,
    }
}

export default useTppElementSupport
